import isUrl from 'is-url-superb'
import { Colors } from '@opallabs/pulsar'
import { warn } from '@opallabs/quanta'
import alphabetize from './alphabetize'
import bucketize from './bucketize'
import formatAssetUrl from './formatAssetUrl'
import formatMentions from './formatMentions'
import { hooks } from './hooks'
import { HotKeyManager, registerHotKey, unregisterHotKey } from './hotKeys'
import isFunction from './isFunction'
import {
  kuiperClassNames,
  kuiperClassNamesWithTracking,
} from './kuiperClassNames'
import { navigateToContent, navigateToMoment } from './navigate'
import { renderIcon } from './renderIcon'

export {
  alphabetize,
  bucketize,
  kuiperClassNames,
  kuiperClassNamesWithTracking,
  formatAssetUrl,
  formatMentions,
  isFunction,
  hooks,
  HotKeyManager,
  navigateToContent,
  navigateToMoment,
  registerHotKey,
  renderIcon,
  unregisterHotKey,
}

export function callWithContext<T>(func: (arg: T) => unknown, context: T) {
  return () => func(context)
}

export function stopPropagation(e: Event) {
  if (e) {
    if (e.stopPropagation && typeof e.stopPropagation === 'function') {
      e.stopPropagation()
    }
  }
}

export function preventDefault(e: Event) {
  if (e) {
    if (e.preventDefault && typeof e.preventDefault === 'function') {
      e.preventDefault()
    }
  }
}

export function stopEvent(e: Event) {
  if (e) {
    stopPropagation(e)
    preventDefault(e)
  }
}

export function noPropagate(func: (e: Event) => void): (e: Event) => void {
  return (...args) => {
    stopEvent(...args)
    func(...args)
  }
}

export function noDefault(func: (e: Event) => void): (e: Event) => void {
  return (e) => {
    preventDefault(e)
    func(e)
  }
}

// Colors is an import from a .json file and typed as a const object,
// so Typescript won't let us access it with a dynamic string property. This
// more general type will let us access it dynamically without strictly
// known names and variants.
type GeneralColorType = Record<string, Record<string, string | undefined>>

export function getColor(color: string, variant = 'base'): string | null {
  const colorMap = Colors as GeneralColorType

  if (!color) {
    return null
  }
  if (colorMap[color.replace('color-', '')]) {
    return colorMap[color.replace('color-', '')][variant] ?? null
  }
  if (colorMap[`color-${color}`]) {
    return colorMap[`color-${color}`][variant] ?? null
  }
  if (/^#/.test(color)) {
    return color
  }
  return null
}

export const noop = () => {
  const err = new Error()
  warn(
    'Noop function run, please ensure each callback has a function passed.',
    err.stack
  )
}

export const returnValidUrl = (url: string) => {
  if (!isUrl(url)) {
    if (url.includes('https://')) {
      return url
    }

    if (url.includes('http://')) {
      return url.replace('http://', 'https://')
    }

    return `https://${url}`
  }

  return url
}

export const prettyUrl = (url: string) => url.replace(/(^\w+:|^)\/\//, '')

/*
 * Returns an Array with any null values filtered out.
 * The primary need for this is for components that may, for one reason or another,
 * end up with null values in its 'children' Array.
 * Note that if 'children' is not an Array, the local copy
 * will be converted to a single-element Array before proceeding.
 */
export const nonNullChildren = <T>(children: T | Array<T>): Array<T> => {
  let childrenArray = children
  if (!Array.isArray(childrenArray)) {
    childrenArray = [children] as Array<T>
  }
  return childrenArray.filter((c) => c)
}

export default {
  alphabetize,
  bucketize,
  callWithContext,
  kuiperClassNames,
  kuiperClassNamesWithTracking,
  formatAssetUrl,
  formatMentions,
  hooks,
  noDefault,
  noPropagate,
  prettyUrl,
  registerHotKey,
  renderIcon,
  returnValidUrl,
  unregisterHotKey,
}
